import { ability } from '@/plugins/casl/ability'
import { useApi } from '@/utils/api'
import { defineStore } from 'pinia'
import { useLocalStorage } from '@vueuse/core'

// Addendum 12, Modul BA: subject CASL yang harus disembunyikan dari sidebar
// (via @layouts/components/*NavLink.vue, yang cuma cek `can(item.action,
// item.subject)` — file itu "do not modify" per CLAUDE.md) kalau
// feature_key terkait tidak aktif untuk tenant. TIDAK termasuk 'Customer'
// (crm_broadcast) sengaja — CustomerController dipakai juga untuk lookup
// customer biasa di checkout POS (Modul R Addendum 07), bukan cuma CRM.
//
// 'PosDisplay' (customer_display, patch Addendum 12 gap) SENGAJA subject
// baru, BUKAN 'Sales' — endpoint pairing CDS memang digerbang permission
// Sales.create di backend (siapa pun yang boleh jualan boleh pairing CDS),
// TAPI menyamakan subject CASL-nya ke 'Sales' akan membuat rule "cannot"
// di bawah menolak SEMUA aksi Sales (checkout, lihat order, dst) untuk
// tenant non-Premium — merusak POS itu sendiri, bukan cuma CDS. Tombol
// "Hubungkan Layar Pelanggan" di pages/pos/index.vue TIDAK dicek lewat
// ability.can('...', 'PosDisplay') (tidak ada role manapun yang otomatis
// dapat permission ke subject baru ini, jadi kalau dipakai literal, tombol
// akan selalu tersembunyi untuk non-owner) — dicek langsung lewat
// authStore.enabledFeatures.customer_display, entry di sini disiapkan
// untuk halaman/menu CDS masa depan yang mungkin punya subject sendiri.
const FEATURE_GATED_SUBJECTS = {
  promo_voucher: ['Promotion', 'Voucher'],
  crm_broadcast: ['Broadcast'],
  customer_display: ['PosDisplay'],
}

/**
 * Owner dapat SATU rule wildcard {action:'manage', subject:'all'} dari
 * backend (getCaslAbilities()), BUKAN rule per-subject seperti role lain —
 * jadi menyaring/menghapus rule Promotion/Voucher/Broadcast dari array TIDAK
 * CUKUP untuk owner (wildcard-nya tetap meng-cover subject apa pun). Solusi:
 * TAMBAHKAN rule "cannot" (inverted:true) untuk subject yang di-gate di
 * AKHIR array — CASL mengevaluasi rule berurutan, rule TERAKHIR yang cocok
 * menang, jadi "cannot" ini selalu mengalahkan wildcard 'manage all' MAUPUN
 * rule spesifik role lain, tanpa perlu tahu struktur rule aslinya sama sekali.
 */
function applyFeatureGating(rules, enabledFeatures) {
  if (!Array.isArray(rules)) return rules

  const denyRules = Object.entries(FEATURE_GATED_SUBJECTS)
    .filter(([featureKey]) => enabledFeatures?.[featureKey] === false)
    .flatMap(([, subjects]) => subjects.map(subject => ({ action: 'manage', subject, inverted: true })))

  return denyRules.length ? [...rules, ...denyRules] : rules
}

export const useAuthStore = defineStore('auth', () => {
  // =========================================================================
  // COOKIES — nama konsisten dengan guards.js
  // =========================================================================
  const userDataCookie = useCookie('userData')
  const accessTokenCookie = useCookie('accessToken')
  const currentTenantCookie = useCookie('current_tenant')

  // Regresi Modul D: ability array (userAbilityRules) dulu cookie, bisa
  // >4KB (mis. role manager 72 permission) dan lewat batas ukuran cookie
  // browser -> DIAM-DIAM gagal tersimpan. Pindah ke localStorage.
  const userAbilityRulesStorage = useLocalStorage('userAbilityRules', [])

  // Addendum 12, Modul BA: peta feature_key -> aktif/tidak dari
  // TenantFeatureService (backend, 1 sumber yang sama dipakai untuk
  // pengecekan endpoint) — dipakai frontend buat menyembunyikan menu
  // sidebar untuk fitur yang tidak tersedia di tenant ini (Promo/Voucher,
  // CRM/Broadcast, dst). Sama pola persis dengan userAbilityRulesStorage
  // di atas (localStorage, diisi ulang tiap login/fetchUser).
  const enabledFeaturesStorage = useLocalStorage('enabledFeatures', {})

  // =========================================================================
  // STATE (reactive dari cookie)
  // =========================================================================
  const user = computed({
    get: () => userDataCookie.value,
    set: val => { userDataCookie.value = val },
  })

  const token = computed({
    get: () => accessTokenCookie.value,
    set: val => { accessTokenCookie.value = val },
  })

  const abilities = computed({
    get: () => userAbilityRulesStorage.value ?? [],
    set: val => { userAbilityRulesStorage.value = val },
  })

  const enabledFeatures = computed({
    get: () => enabledFeaturesStorage.value ?? {},
    set: val => { enabledFeaturesStorage.value = val },
  })

  const tenantSlug = computed({
    get: () => currentTenantCookie.value,
    set: val => { currentTenantCookie.value = val },
  })

  // =========================================================================
  // GETTERS
  // =========================================================================
  const isAuthenticated = computed(() => !!user.value && !!token.value)
  const currentTenant = computed(() => tenantSlug.value)

  // =========================================================================
  // ACTIONS
  // =========================================================================

  /**
   * Login — kirim credentials ke /api/v1/auth/login
   *
   * ✅ FIX: useApi di utils/api.js pakai ofetch.
   * ofetch membaca body dari key `body`, bukan `data`.
   * Pass objek langsung ke post({ body: credentials }).
   */
  const login = async (credentials) => {
    const data = await useApi('/api/v1/auth/login', {
      headers: { 'X-Tenant': credentials.tenant_slug },
    }).post({ body: credentials })

    // ✅ FIX: ofetch mengembalikan response langsung (tidak dibungkus { data })
    // Backend kita: ApiResponse::success({ user, token, userAbilities })
    // → ofetch sudah auto-parse JSON, hasilnya adalah object response penuh
    const responseData = data?.data ?? data

    // Set cookies (jalur normal via reactive useCookie)
    userDataCookie.value = responseData.user
    accessTokenCookie.value = responseData.token
    currentTenantCookie.value = credentials.tenant_slug

    // localStorage — bukan cookie, jadi tidak kena masalah ukuran ~4KB &
    // watcher-async yang dijelaskan di bawah untuk cookie. useLocalStorage
    // (VueUse) menulis langsung ke localStorage.setItem() di dalam setter-nya
    // sendiri, jadi tidak perlu fallback manual.
    enabledFeaturesStorage.value = responseData.enabledFeatures ?? {}
    userAbilityRulesStorage.value = applyFeatureGating(responseData.userAbilities ?? [], enabledFeaturesStorage.value)

    // ⚠️ FALLBACK: tulis manual langsung ke document.cookie, JANGAN cuma
    // andalkan watcher useCookie(). ADA DUA alasan berbeda kenapa watcher
    // itu bisa gagal diam-diam:
    //  1. Untuk value object (userData) — watcher-nya bersifat async dan
    //     bisa gagal diam-diam untuk tipe non-string.
    //  2. Untuk value string sederhana seperti current_tenant/accessToken —
    //     kalau value baru PERSIS SAMA dengan value lama yang masih
    //     tersimpan di ref (misal: login ke tenant yang sama dua kali
    //     berturut-turut, atau ref belum sempat di-null-kan oleh proses
    //     logout sebelumnya), Vue MENGANGGAP TIDAK ADA PERUBAHAN dan watcher
    //     TIDAK PERNAH DIJALANKAN sama sekali — cookie fisik di browser
    //     tidak pernah ditulis ulang. Ini persis bug yang pernah membuat
    //     current_tenant hilang setelah logout->login ke tenant yang sama,
    //     menyebabkan seluruh API balas "Tenant tidak ditemukan" (404).
    // Menulis manual ke document.cookie di bawah ini TIDAK bergantung pada
    // reactivity Vue sama sekali, jadi selalu jalan apapun kondisinya.
    const maxAge = 60 * 60 * 24 * 30

    document.cookie = `userData=${encodeURIComponent(JSON.stringify(responseData.user))}; path=/; max-age=${maxAge}`
    document.cookie = `accessToken=${encodeURIComponent(responseData.token)}; path=/; max-age=${maxAge}`
    document.cookie = `current_tenant=${encodeURIComponent(credentials.tenant_slug)}; path=/; max-age=${maxAge}`

    // Verifikasi — kalau masih gagal, ini akan kelihatan jelas di console
    const verify = document.cookie.includes('userData=')
      && document.cookie.includes('accessToken=')
      && document.cookie.includes('current_tenant=')

    if (!verify) {
      console.error('[AUTH] Gagal menyimpan salah satu cookie auth/tenant. document.cookie:', document.cookie)
    }

    // Update CASL ability instance — pakai rules yang SUDAH digating fitur
    // (userAbilityRulesStorage.value), bukan responseData.userAbilities
    // mentah, supaya menu Promo/Voucher/Broadcast konsisten tersembunyi
    // untuk tenant yang fiturnya tidak aktif (Modul BA).
    ability.update(userAbilityRulesStorage.value)

    return responseData
  }

  const logout = async () => {
    try {
      await useApi('/api/v1/auth/logout').post({ body: {} })
    } catch (e) {
      console.warn('Logout API error:', e)
    } finally {
      userDataCookie.value = null
      accessTokenCookie.value = null
      userAbilityRulesStorage.value = []
      enabledFeaturesStorage.value = {}
      currentTenantCookie.value = null

      // Sama seperti di login() — jangan cuma andalkan watcher useCookie().
      // Hapus juga langsung lewat document.cookie supaya cookie fisik pasti
      // hilang, tidak bergantung pada apakah Vue menganggap value "berubah".
      document.cookie = 'userData=; path=/; max-age=-1'
      document.cookie = 'accessToken=; path=/; max-age=-1'
      document.cookie = 'current_tenant=; path=/; max-age=-1'

      ability.update([])
    }
  }

  const fetchUser = async () => {
    try {
      const data = await useApi('/api/v1/auth/me').get()
      const responseData = data?.data ?? data

      userDataCookie.value = responseData.user

      if (responseData.enabledFeatures) {
        enabledFeaturesStorage.value = responseData.enabledFeatures
      }

      if (responseData.userAbilities) {
        const gatedRules = applyFeatureGating(responseData.userAbilities, enabledFeaturesStorage.value)

        userAbilityRulesStorage.value = gatedRules
        ability.update(gatedRules)
      }

      return responseData.user
    } catch (e) {
      userDataCookie.value = null
      accessTokenCookie.value = null
      userAbilityRulesStorage.value = []
      enabledFeaturesStorage.value = {}

      return null
    }
  }

  return {
    user,
    token,
    abilities,
    enabledFeatures,
    tenantSlug,
    isAuthenticated,
    currentTenant,
    login,
    logout,
    fetchUser,
  }
})
